import { storyblokEditable } from "@storyblok/react/rsc";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { getStoryblokImageUrl } from "@/lib/image-utils";
import { getUrlFromMultilink } from "@/lib/utils";
import type { LogoCloud as LogoCloudBlok } from "@/types/storyblok";

export default function LogoCloud({ blok }: { blok: LogoCloudBlok }) {
  const subtitleLinkUrl = blok.subtitle_link
    ? getUrlFromMultilink(blok.subtitle_link)
    : null;

  return (
    <div
      className="relative"
      // biome-ignore lint/suspicious/noExplicitAny: Required for Storyblok React compatibility
      {...storyblokEditable(blok as any)}
    >
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 relative isolate pt-24 pb-48">
        <div className="sm:text-center flex flex-col space-y-4 max-w-screen-lg mx-auto">
          {blok.subtitle &&
            (subtitleLinkUrl ? (
              <Link
                href={subtitleLinkUrl}
                className="text-2xl drop-shadow-2xl w-fit sm:mx-auto font-medium font-display uppercase text-teal-500 hover:text-teal-600 hover:scale-98 transition-all duration-300"
              >
                <span className="flex items-center gap-2 justify-center">
                  {blok.subtitle} <ArrowRight />
                </span>
              </Link>
            ) : (
              <p className="text-2xl drop-shadow-2xl w-fit sm:mx-auto font-medium font-display uppercase text-teal-500">
                {blok.subtitle}
              </p>
            ))}
          {blok.title && (
            <h2 className="text-5xl md:text-6xl drop-shadow-2xl font-extrabold font-display bg-gradient-to-br from-violet-100 via-zinc-50 to-violet-200 bg-clip-text text-transparent leading-tight pb-1">
              {blok.title}
            </h2>
          )}
          {blok.lead && (
            <p className="text-xl drop-shadow-2xl font-medium text-muted-foreground mt-4 sm:mt-6 mb-8 max-w-prose mx-auto">
              {blok.lead}
            </p>
          )}
        </div>
        {blok.logos && blok.logos.length > 0 && (
          <div className="grid grid-cols-2 items-center sm:grid-cols-4 md:grid-cols-6 xl:grid-cols-8 mt-12 gap-12 justify-center">
            {blok.logos.slice(0, 16).map((logo, index) => {
              const imageUrl = getStoryblokImageUrl(logo, {
                width: 200,
                quality: 80,
                format: "webp",
              });

              if (!imageUrl) return null;

              return (
                <div
                  key={logo.id || `logo-${index}`}
                  className={
                    index >= 12
                      ? "hidden xl:block"
                      : index >= 8
                        ? "hidden md:block"
                        : undefined
                  }
                >
                  <Image
                    src={imageUrl}
                    alt={logo.alt || logo.name || "Logo"}
                    width={logo.width || 600}
                    height={logo.height || 600}
                    className="w-full h-auto brightness-0 invert transition-all duration-300"
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
